import { Component, useState, type ReactNode } from 'react';
import { captureError } from '../lib/posthog';
import { Button } from './primitives';
import { BugReportModal } from './BugReportModal';

type Props = { children: ReactNode; fallback: ReactNode };
type State = { hasError: boolean };

// Catches render-time crashes anywhere below it, reports them to PostHog error
// tracking, and shows a recoverable fallback instead of a blank white screen.
//
// There are two: one around <App/> as the last resort, and one around the routed
// screen, keyed on the pathname, so a screen that throws leaves the sidebar, the
// topbar and Report a bug standing, and going to another screen clears it
// (SH-12). With only the outer one, any screen crash took the whole shell.
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: { componentStack?: string | null }) {
    captureError(error, { componentStack: info.componentStack ?? undefined });
  }

  render() {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}

// Both fallbacks: tokens and the shared Button, in the body's own typeface. The
// old one set system-ui and drew its own button, so the one screen that tells
// someone the app broke did not look like the app.
const Crash = ({
  title,
  detail,
  page,
  children,
}: {
  title: string;
  detail: string;
  /** The whole page (the root boundary): an h1 on the paper, full height. */
  page?: boolean;
  children: ReactNode;
}) => {
  const Heading = page ? 'h1' : 'h2';
  return (
    <div
      role="alert"
      style={{
        minHeight: page ? '100vh' : '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--hf-space-3)',
        padding: 'var(--hf-space-5)',
        textAlign: 'center',
        color: 'var(--hf-ink)',
        ...(page ? { background: 'var(--hf-paper)' } : {}),
      }}
    >
      <Heading
        style={{
          margin: 0,
          fontFamily: 'var(--hf-font-display)',
          fontSize: 'var(--hf-text-xl)',
          fontWeight: 'var(--hf-weight-medium)',
          letterSpacing: '-0.02em',
        }}
      >
        {title}
      </Heading>
      <p
        style={{
          margin: 0,
          maxWidth: 420,
          fontSize: 'var(--hf-text-md)',
          lineHeight: 'var(--hf-leading-body)',
          color: 'var(--hf-ink-3)',
        }}
      >
        {detail}
      </p>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: 'var(--hf-space-2)',
          marginTop: 'var(--hf-space-2)',
        }}
      >
        {children}
      </div>
    </div>
  );
};

const Reload = () => (
  <Button variant="primary" onClick={() => window.location.reload()}>
    Reload
  </Button>
);

/** The last resort, around the whole app: nothing of the shell is left to use. */
export const RootErrorFallback = () => (
  <Crash
    page
    title="Something went wrong"
    detail="The error has been reported. Reloading usually fixes it. If it keeps happening, contact your administrator."
  >
    <Reload />
  </Crash>
);

/** Inside the shell, in place of the screen that threw. */
export const ScreenErrorFallback = ({
  screen,
  screenLabel,
}: {
  screen: string;
  screenLabel: string;
}) => {
  const [reporting, setReporting] = useState(false);
  return (
    <>
      <Crash
        title="This screen hit an error"
        detail="The error has been reported, and the rest of the app still works. Reload to try this screen again, or pick another from the sidebar."
      >
        <Reload />
        <Button icon="bug" onClick={() => setReporting(true)}>
          Report it
        </Button>
      </Crash>
      {/* Beside the alert, not in it: a dialog inside role="alert" is read out
          whole as part of the alert. */}
      {reporting && (
        <BugReportModal
          screen={screen}
          screenLabel={screenLabel}
          onClose={() => setReporting(false)}
        />
      )}
    </>
  );
};
